import { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Button from 'components/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import InfoOutline from '@mui/icons-material/InfoOutline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { match } from 'ts-pattern';
import { Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Switch from 'further-ui/components/Switch';
import { BulletinDeliveryMethod } from 'further-types/lp/bulletin-update';
import { LpInvestorUpdateStatus } from 'further-types/lp/investor-update';
import { LP_INVESTOR_UPDATES } from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import Page from 'components/new/Page';
import Stepper from 'components/new/Stepper';
import AddEditBulletinUpdateForm from './AddEditBulletinUpdateForm';
import Complete from './Complete';
import useBulletinUpdate from './useBulletinUpdate';
import useCreationStep, { CreationStep } from './useCreationStep';
import { BulletinUpdateFormValues, schema } from './schema';
import {
  mapBulletinUpdateToFormValues,
  mapFormValuesToSaveInput,
} from './formMapping';
import { BulletinUpdate } from './types';

const StyledStepper = styled(Stepper)(() => ({
  '& .MuiStepConnector-line': {
    minWidth: 80,
    flexShrink: 0,
  },
}));

type Props = {
  bulletinUpdate: BulletinUpdate;
};

const AddEditBulletinUpdate: FC<Props> = ({ bulletinUpdate }) => {
  const { id: bulletinUpdateId } = bulletinUpdate;
  const { save, publish, saving } = useBulletinUpdate();

  const history = useHistory();
  const { pathname, search } = useLocation<{ bulletinUpdateId?: string }>();
  const { error } = useNotification();
  const publishDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const [hasCompletedCreateStep, setHasCompletedCreateStep] = useState(
    !!bulletinUpdateId,
  );

  const ensureBulletinUpdateIdInQuery = (id: string) => {
    const params = new URLSearchParams(search);
    if (params.get('bulletinUpdateId') === id) return;

    params.set('bulletinUpdateId', id);
    history.replace(`${pathname}?${params.toString()}`);
  };

  const formValues = mapBulletinUpdateToFormValues(bulletinUpdate);

  const {
    setValue,
    getValues,
    formState: { isDirty },
    ...form
  } = useForm<BulletinUpdateFormValues>({
    resolver: zodResolver(schema),
    values: formValues,
  });

  const dataIsValid = schema.safeParse({
    ...formValues,
    useFullValidation: true,
  }).success;

  const { step, setStep } = useCreationStep(
    dataIsValid || hasCompletedCreateStep,
  );

  const isDraft = bulletinUpdate.status === LpInvestorUpdateStatus.Draft;
  const isPublished =
    bulletinUpdate.status === LpInvestorUpdateStatus.Published;
  const deliveryMethod = form.watch('deliveryMethod');
  const investorsWillBeNotified = match(deliveryMethod)
    .with(BulletinDeliveryMethod.Portal, () => false)
    .otherwise(() => true);
  const resendPublishedUpdates = form.watch('resendPublishedUpdates');
  const emailsWillBeSent = match({
    isPublished,
    investorsWillBeNotified,
    resendPublishedUpdates,
  })
    .with(
      { isPublished: true },
      ({ investorsWillBeNotified, resendPublishedUpdates }) =>
        resendPublishedUpdates && investorsWillBeNotified,
    )
    .otherwise(() => investorsWillBeNotified);

  const handleProceed = () => {
    if (saving) return;

    setValue('useFullValidation', true);
    form.handleSubmit(
      (formData) => {
        if (!formData.useFullValidation) return;
        if (step === CreationStep.CreateUpdate) {
          setHasCompletedCreateStep(true);
          setStep(CreationStep.ReviewAndPublish);
        } else if (publishDialog.isOpen) {
          publish(mapFormValuesToSaveInput(bulletinUpdate, formData), {
            onSuccess: ({ id }) => {
              if (id) {
                if (!bulletinUpdateId) {
                  ensureBulletinUpdateIdInQuery(id);
                }
                setValue('id', id);
              }
              publishDialog.onClose();
              setStep(CreationStep.Complete);
            },
          });
        } else {
          publishDialog.onOpen();
        }
      },
      () => {
        error('Please check the page for errors before proceeding.');
      },
    )();
  };

  const handleRetreat = () => {
    if (step === CreationStep.ReviewAndPublish) {
      setStep(CreationStep.CreateUpdate);
    } else if (
      step === CreationStep.CreateUpdate &&
      !cancelDialog.isOpen &&
      isDirty
    ) {
      cancelDialog.onOpen();
    } else {
      history.push(LP_INVESTOR_UPDATES);
    }
  };

  const handleSaveAndExit = () => {
    if (saving) return;
    setValue('useFullValidation', false);
    form.clearErrors();
    form.handleSubmit(
      (formData) => {
        const mappedData = mapFormValuesToSaveInput(bulletinUpdate, formData);
        save(mappedData, {
          onSuccess: ({ id }) => {
            if (id) {
              if (!bulletinUpdateId) {
                ensureBulletinUpdateIdInQuery(id);
              }
              setValue('id', id);
            }
            history.push(LP_INVESTOR_UPDATES);
          },
        });
      },
      () => {
        error(
          'Unable to save your changes, please check the page for errors before proceeding.',
        );
      },
    )();
  };

  return (
    <Page
      tabTitle="Investor Updates: Bulletin update"
      goBackAction={LP_INVESTOR_UPDATES}
      heading="Bulletin update"
      withHeadingDivider
      withStickyHeader
      headerBreakpoint="sm"
      customActions={
        <StyledStepper
          steps={[
            bulletinUpdateId ? 'Edit update' : 'Create update',
            'Review and publish',
          ]}
          activeStep={match(step)
            .with(CreationStep.Complete, () => 2)
            .with(CreationStep.ReviewAndPublish, () => 1)
            .otherwise(() => 0)}
        />
      }
      spacing="lg"
      actionBar={
        <Row
          spacing="sm"
          justify={
            step === CreationStep.Complete ? 'flex-end' : 'space-between'
          }
          breakpoint="sm"
          reverse="breakpoint"
        >
          {step === CreationStep.Complete ? (
            <Button onClick={handleRetreat} variant="outlined" size="large">
              Exit
            </Button>
          ) : (
            <>
              <Button size="large" variant="outlined" onClick={handleRetreat}>
                {step === CreationStep.CreateUpdate
                  ? 'Cancel'
                  : 'Previous step'}
              </Button>
              <Row spacing="sm" centered breakpoint="sm" reverse="breakpoint">
                {isDraft ? (
                  <Button
                    variant="outlined"
                    size="large"
                    whiteSpace="nowrap"
                    loading={saving}
                    onClick={handleSaveAndExit}
                  >
                    Save and exit
                  </Button>
                ) : null}
                {step === CreationStep.ReviewAndPublish && isPublished ? (
                  <Row centered>
                    <Tooltip title="Toggle this on if you want to resend email notifications to investors when you re-publish this bulletin update.">
                      <span>
                        <Row spacing="xxs" centered>
                          <Typography variant="h6">Resend updates</Typography>
                          <InfoOutline fontSize="inherit" />
                        </Row>
                      </span>
                    </Tooltip>
                    <Controller
                      control={form.control}
                      name="resendPublishedUpdates"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Row>
                ) : null}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleProceed}
                  disabled={saving}
                >
                  {step === CreationStep.CreateUpdate
                    ? 'Continue'
                    : 'Publish update'}
                </Button>
              </Row>
            </>
          )}
        </Row>
      }
    >
      <ConfirmDialog
        maxWidth="sm"
        content={`You are about to publish this update and investors will${
          !emailsWillBeSent ? ' not' : ''
        } be notified by email. Do you wish to proceed?`}
        title="Publishing update"
        loading={saving}
        actions={{
          confirm: {
            onClick: handleProceed,
            label: 'Publish update',
          },
        }}
        onClose={publishDialog.onClose}
        open={publishDialog.isOpen}
      />
      <ConfirmDialog
        maxWidth="sm"
        content={
          isDraft
            ? 'You are about to leave without saving. All progress will be lost. Do you want to save before exiting?'
            : 'You are about to exit without submitting this update. Any unsaved changes will be lost.'
        }
        title={bulletinUpdateId ? 'Exit update' : 'Exit update creation'}
        actions={{
          alternateConfirm: isDraft
            ? {
                onClick: handleRetreat,
                label: 'Exit without saving',
              }
            : undefined,
          confirm: isDraft
            ? {
                onClick: handleSaveAndExit,
                label: 'Save and exit',
              }
            : {
                onClick: handleRetreat,
                label: 'Exit without submitting',
              },
        }}
        onClose={cancelDialog.onClose}
        open={cancelDialog.isOpen}
      />
      {step === CreationStep.Complete ? (
        <Complete />
      ) : (
        <AddEditBulletinUpdateForm
          control={form.control}
          getValues={getValues}
          readOnly={step === CreationStep.ReviewAndPublish}
          hasUkInvestorWithCommitment={
            bulletinUpdate.hasUkInvestorWithCommitment
          }
        />
      )}
    </Page>
  );
};

export default AddEditBulletinUpdate;
